'use client'

import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode } from 'react'

/**
 * Leave guards for surfaces holding unsaved work (plan 2026-08-12-001 U10, R12).
 *
 * A surface with a draft registers a guard here; a control that moves the user
 * somewhere the draft will not survive asks `confirmLeave()` first and aborts on
 * `false`. It exists for the exits no browser or router event reports: switching
 * the active school (the URL stays put and the editor remounts under the new
 * school) and a programmatic `router.push` (the command palette). Links, Back
 * and window close are caught where the draft lives, by its own listeners.
 *
 * The `(core)` layout mounts the provider just inside `SchoolProvider`, beside
 * the school switch that needed it. It is a module of its own so that the many
 * tests which mock `school-context` do not also have to stub a registry:
 * outside a provider, `useLeaveGuard` answers "nothing to guard".
 */

/** Returns true when the user may leave (nothing unsaved, or they confirmed). */
type LeaveGuard = () => boolean

interface LeaveGuardValue {
  /** Adds a guard; the returned function removes it. */
  register: (guard: LeaveGuard) => () => void
  /** Runs every registered guard; false as soon as one refuses. */
  confirmLeave: () => boolean
}

const LeaveGuardContext = createContext<LeaveGuardValue | null>(null)

const NO_GUARDS: LeaveGuardValue = { register: () => () => undefined, confirmLeave: () => true }

export function LeaveGuardProvider({ children }: { children: ReactNode }) {
  // A ref, not state: registering must not re-render the whole school tree, and
  // the guards read their own current state when they run.
  const guards = useRef(new Set<LeaveGuard>())

  const register = useCallback((guard: LeaveGuard) => {
    guards.current.add(guard)
    return () => {
      guards.current.delete(guard)
    }
  }, [])

  const confirmLeave = useCallback(() => [...guards.current].every(guard => guard()), [])

  const value = useMemo(() => ({ register, confirmLeave }), [register, confirmLeave])
  return <LeaveGuardContext.Provider value={value}>{children}</LeaveGuardContext.Provider>
}

export function useLeaveGuard(): LeaveGuardValue {
  return useContext(LeaveGuardContext) ?? NO_GUARDS
}
